import React, { ReactNode } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from '@reach/router'
import { useStaticQuery, graphql } from 'gatsby'
import { ICourse2 } from '@et/types/Courses'
import { IGatsbyChildImageSharp } from '@et/types/Gatsby'
import {
  facebookMeta, renderMissMagnolia, renderSentinel,
  twitterMeta,
} from '@utils/genUtils'

interface IProps {
  course?: ICourse2
  sentinel?: boolean
  proof?: boolean
  deadlineFunnel?: boolean
  classCard: IGatsbyChildImageSharp | string
  children?: ReactNode
}


const GatsbySEO = ({ course, children, classCard, sentinel = false, proof, deadlineFunnel }: IProps) => {
  const { pathname } = useLocation()
  const { site } = useStaticQuery(query)

  function getClassCard(url: string): string {
    if (typeof classCard === 'object' && classCard.childImageSharp.gatsbyImageData.images.fallback) {
      return url + classCard.childImageSharp.gatsbyImageData.images.fallback.src
    }
    if (typeof classCard === 'string') {
      return classCard
    }
    return ''
  }

  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
  } = site.siteMetadata
  const defaultKeywords = ['']
  const courseImg = getClassCard(siteUrl)


  const seo = {
    title: course ? course.title : defaultTitle,
    titleTemplate: `%s | ${site.siteMetadata.defaultTitle}`,
    description: course ? course.classDescriptor : defaultDescription,
    url: `${siteUrl}${pathname}`,
  }
  const meta = [
    {
      name: 'googlebot',
      content: 'index,follow',
    },
    {
      name: 'robots',
      content: 'index,follow',
    },
    {
      name: 'theme-color',
      content: '#f715ab',
    },
    {
      name: `description`,
      content: seo.description,
    },
  ].concat(
    course && course.keywords.length > 0
      ? {
        name: `keywords`,
        content: course.keywords.join(`, `),
      }
      : {
        name: `keywords`,
        content: defaultKeywords.join(`, `),
      })
    .concat([
      ...twitterMeta({
        course,
        siteUrl,
        socialImg: courseImg,
      }),
    ])
  const facebookMetaProperty = [
    ...facebookMeta({
      course,
      siteUrl,
      url: `${siteUrl}${pathname}`,
      socialImg: courseImg,
    }),
  ]

  return (
    <Helmet
      title={seo.title}
      titleTemplate={seo.titleTemplate}
    >

      <meta name='facebook-domain-verification' content='etgpz91wfo9t6ld5e3aoap3cozeu1s' />
      <meta name='image' content={courseImg} />
      <link rel='canonical' href={`${siteUrl}${pathname}`} />
      {meta.map((item, index) => (
        <meta key={index} name={item.name} content={item.content} />
      ))}

      {facebookMetaProperty.map((item, index) => (
        <meta key={index} property={item.property} content={item.content} />
      ))}

      {sentinel ? renderSentinel() : renderMissMagnolia()}

      {/*<!--PROOF PIXEL-->*/}
      {proof && <script src='https://cdn.useproof.com/proof.js?acc=DESOCP5OHCXy7mEB4nQhJWdeRPA3' async={true} />}
      {/*<!--PROOF PIXEL-->*/}

      {deadlineFunnel && <script type="text/javascript" data-cfasync="false" src='/js/df.js' />}



      {children && children}
    </Helmet>
  )
}

export default GatsbySEO

const query = graphql`
    query SEO {
        site {
            siteMetadata {
                defaultTitle: title
                defaultDescription: description
                siteUrl: siteUrl
            }
        }
    }
`
